import { updateMapData } from '@bpd-library/components';
import {
    ExtendedAreaModels,
    ExtendedConstructionNumber,
    findApartmentBuilding,
} from '@bpd-library/utilities';
import { getFilteredData, getSoldProjectsData, updateView } from '@features/properties/store';
import { apartmentsViewEvents } from '@molecules/apartments-view/utilities/constants';
import { hasConsent } from '@store/service-consent';
import getExtendedAreaMapData from '@utilities/api-utilities/get-extended-area-map-data';
import { useAreaData } from '@utilities/hooks';
import { Route, RouteHandler } from '@utilities/hooks/use-routing';

import { usePropertiesRouting } from '../hooks/use-properties-routing';
import { setPropertiesMapBounds } from '../utilities/bounding-box';

const getApartmentBuildingById = (data?: ExtendedAreaModels, apartmentBuildingId?: string) => {
    if (!data?.apartmentBuildings.length || !apartmentBuildingId) return;
    return data.apartmentBuildings.find(({ id }) => apartmentBuildingId === id);
};

const apartmentViewRoute: RouteHandler = async (route) => {
    const { slug: apartmentBuildingId = '' } = route;
    let buildingName: string | null | undefined;
    let constructionNumbers: ExtendedConstructionNumber[] | undefined;

    const filteredData = getFilteredData();
    const filteredAB = getApartmentBuildingById(filteredData, apartmentBuildingId);

    buildingName = filteredAB?.displayName;
    constructionNumbers = filteredAB?.constructionNumbers?.filter(({ id }) =>
        filteredData?.constructionNumbers.find(({ id: filteredId }) => filteredId === id),
    );

    if (constructionNumbers?.length) {
        setApartmentConstructionNumberView(route, constructionNumbers, buildingName);
        return;
    }

    const soldData = getSoldProjectsData();
    const soldAB = getApartmentBuildingById(soldData, apartmentBuildingId);

    buildingName = soldAB?.displayName;
    constructionNumbers = soldAB?.constructionNumbers;

    if (constructionNumbers?.length) {
        setApartmentConstructionNumberView(route, constructionNumbers, buildingName);
        return;
    }

    const nonFilteredData = await useAreaData();
    const nonFilteredAB = getApartmentBuildingById(nonFilteredData, apartmentBuildingId);

    buildingName = nonFilteredAB?.displayName;
    constructionNumbers = nonFilteredAB?.constructionNumbers;

    if (
        (nonFilteredAB?.status === 'Sold' || nonFilteredAB?.status === 'UnderConstruction') &&
        constructionNumbers?.length
    ) {
        setApartmentConstructionNumberView(route, constructionNumbers, buildingName);
        return;
    }

    setApartmentPropertyTypeView(route, filteredData, apartmentBuildingId, buildingName);
};

/**
 * @description Sets the properties view to an apartment view with construction numbers
 * @param {ExtendedConstructionNumber[]} constructionNumbers
 */
function setApartmentConstructionNumberView(
    { slug, viewId }: Route,
    constructionNumbers: ExtendedConstructionNumber[],
    buildingName?: string | null,
) {
    const { setRoute } = usePropertiesRouting();

    updateView({
        type: 'apartmentConstructionNumber',
        data: constructionNumbers,
        slug,
        viewId,
        name: buildingName,
        onReturn: () => {
            setRoute({
                to: 'propertyType',
            });
        },
    });

    document.dispatchEvent(
        new CustomEvent(apartmentsViewEvents.ALIAS_CHANGED, {
            detail: {
                constructionNumberId: constructionNumbers[0].id,
            },
        }),
    );
}

/**
 * @description Sets the properties view to apartment view with property types
 * @param {ExtendedAreaModels} data
 * @param {string} [apartmentBuildingId]
 */
function setApartmentPropertyTypeView(
    { slug, viewId }: Route,
    data?: ExtendedAreaModels,
    apartmentBuildingId?: string,
    buildingName?: string | null,
) {
    const { setRoute } = usePropertiesRouting();

    if (!data) return;

    const apartmentBuilding = findApartmentBuilding(data.apartmentBuildings, {
        id: apartmentBuildingId,
    });

    const propertyTypes = data.propertyTypes?.filter((s) =>
        s.apartmentBuildings?.find(
            (apartmentBuilding) => apartmentBuilding.id === apartmentBuildingId,
        ),
    );

    updateView({
        type: 'propertyType',
        data: propertyTypes,
        slug,
        viewId,
        name: buildingName,
        onReturn: () => {
            setRoute({
                to: 'propertyType',
            });
        },
    });

    if (hasConsent('mapbox')) {
        const mapData = getExtendedAreaMapData({
            propertyTypes,
            apartmentBuildings: apartmentBuilding ? [apartmentBuilding] : [],
        });

        updateMapData(mapData);
        setPropertiesMapBounds(data, mapData);
    }
}

export { apartmentViewRoute };
